// src/components/dashboard/Workshop4Dashboard.js
// Professional Workshop 4 Dashboard using Tremor layout + ECharts
import React from 'react';
import {
  Card,
  Title,
  Text,
  Metric,
  Grid,
  Flex,
  Badge,
  BadgeDelta
} from '@tremor/react';
import { useTranslation } from 'react-i18next';
import {
  Zap,
  Target,
  Activity,
  AlertTriangle
} from 'lucide-react';
import EChart from './charts/EChart';
import DashboardGrid from './DashboardGrid';
import { W4_DEFAULT_LAYOUT } from './defaultLayouts';
import {
  buildRiskMatrix,
  selectTopAssets,
  buildSeveritySkillMatrix,
  buildCoverage,
  levelToLabel
} from './charts/workshop4Data';

const Workshop4Dashboard = ({
  attackPaths = [],
  operationalScenarios = [],
  workshop4DashboardData = null,
  isLoading = false,
  editMode = false,
  layouts = null,
  onLayoutChange = null,
  onToggleHidden = null
}) => {
  const { t } = useTranslation();

  // Extract data from workshop4DashboardData
  const overallRiskScore = workshop4DashboardData?.overallRiskScore || 0;
  const topVulnerableAssets = selectTopAssets(workshop4DashboardData?.topAtRiskAssets);

  // Canonical risk matrix: prefer raw scenarios, fall back to precomputed backend matrix
  const matrixSource = operationalScenarios.length > 0
    ? operationalScenarios
    : (workshop4DashboardData?.riskMatrix || []);
  const riskMatrix = buildRiskMatrix(matrixSource);

  // Severity x required-skill heatmap from raw scenarios
  const skillMatrix = buildSeveritySkillMatrix(operationalScenarios);

  // Coverage: attack paths with vs without an operational scenario
  const coverage = buildCoverage(attackPaths, operationalScenarios);

  // Calculate totals
  const totalAttackPaths = attackPaths.length;
  const totalScenarios = operationalScenarios.length;

  // Risk level helper
  const getRiskLevel = (score) => {
    if (score >= 9) return { label: t('dashboard.workshop4.critical'), color: 'rose', deltaType: 'increase' };
    if (score >= 7) return { label: t('dashboard.workshop4.high'), color: 'orange', deltaType: 'increase' };
    if (score >= 4) return { label: t('dashboard.workshop4.medium'), color: 'amber', deltaType: 'unchanged' };
    return { label: t('dashboard.workshop4.low'), color: 'emerald', deltaType: 'decrease' };
  };

  const riskInfo = getRiskLevel(overallRiskScore);

  // Count high-risk assets (score >= 7)
  const highRiskAssets = topVulnerableAssets.filter((a) => a.riskScore >= 7).length;

  // Canonical risk matrix heatmap (x = likelihood 1-4, y = severity 1-4).
  // Cells flagged `defaulted` fell back to the default level (unmapped value):
  // same palette, dashed dark outline only — never a new color.
  const riskMatrixOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const likelihoodLabel = levelToLabel(params.value[0] + 1);
        const severityLabel = levelToLabel(params.value[1] + 1);
        const base = `${severityLabel} x ${likelihoodLabel}: ${params.value[2]}`;
        if (params.data && params.data.defaulted) {
          return `${base}<br />${t('dashboard.workshop4.defaultedLevel')}`;
        }
        return base;
      }
    },
    grid: { left: 8, right: 16, top: 8, bottom: 64, containLabel: true },
    xAxis: {
      type: 'category',
      data: riskMatrix.xLabels,
      name: t('dashboard.workshop4.likelihood'),
      nameLocation: 'middle',
      nameGap: 28
    },
    yAxis: {
      type: 'category',
      data: riskMatrix.yLabels,
      name: t('dashboard.workshop4.severity')
    },
    visualMap: {
      min: 0,
      max: Math.max(riskMatrix.max, 1),
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      inRange: { color: ['#ECFDF5', '#F59E0B', '#DC2626'] }
    },
    series: [{
      type: 'heatmap',
      data: riskMatrix.data,
      label: { show: true },
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0, 0, 0, 0.3)' } }
    }]
  };

  // Severity x required-skill heatmap
  const skillHeatmapOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const severityLabel = skillMatrix.rowLabels[params.value[1]] || '';
        const skillLabel = skillMatrix.columnLabels[params.value[0]] || '';
        return `${severityLabel} • ${skillLabel}: ${params.value[2]}`;
      }
    },
    grid: { left: 8, right: 16, top: 8, bottom: 64, containLabel: true },
    xAxis: {
      type: 'category',
      data: skillMatrix.columnLabels,
      axisLabel: { interval: 0, rotate: 30 }
    },
    yAxis: {
      type: 'category',
      data: skillMatrix.rowLabels,
      axisLabel: { interval: 0 }
    },
    visualMap: {
      min: 0,
      max: Math.max(skillMatrix.max, 1),
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      inRange: { color: ['#EEF2FF', '#6366F1'] }
    },
    series: [{
      type: 'heatmap',
      data: skillMatrix.data,
      label: { show: true },
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0, 0, 0, 0.3)' } }
    }]
  };

  // Coverage donut: attack paths with vs without an operational scenario
  const coveragePct = coverage.total > 0 ? Math.round((coverage.withScenarios / coverage.total) * 100) : 0;
  const coverageOption = {
    tooltip: { trigger: 'item' },
    legend: { bottom: 0 },
    title: {
      text: `${coverage.withScenarios}/${coverage.total}`,
      subtext: `${coveragePct}%`,
      left: 'center',
      top: '32%',
      textStyle: { fontSize: 26, fontWeight: 700, color: '#111827' },
      subtextStyle: { fontSize: 14, color: '#6B7280' }
    },
    series: [{
      type: 'pie',
      radius: ['50%', '70%'],
      center: ['50%', '45%'],
      label: { show: false },
      data: [
        {
          name: t('dashboard.workshop4.withScenarios'),
          value: coverage.withScenarios,
          itemStyle: { color: '#10B981' }
        },
        {
          name: t('dashboard.workshop4.withoutScenarios'),
          value: coverage.withoutScenarios,
          itemStyle: { color: '#94A3B8' }
        }
      ]
    }]
  };

  // Check if we have any data
  const hasData = totalAttackPaths > 0 || totalScenarios > 0 || overallRiskScore > 0;

  if (isLoading) {
    return (
      <Card className="!bg-white">
        <Flex justifyContent="center" alignItems="center" className="h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent mr-3"></div>
          <Text>{t('dashboard.status.loading')}</Text>
        </Flex>
      </Card>
    );
  }

  if (!hasData) {
    return (
      <Card className="!bg-white">
        <Flex justifyContent="center" alignItems="center" className="h-40 flex-col">
          <Zap className="w-12 h-12 text-gray-300 mb-3" />
          <Text className="!text-gray-500">{t('dashboard.workshop4.noData')}</Text>
          <Text className="text-xs !text-gray-400 mt-1">{t('dashboard.workshop4.completeWorkshop4')}</Text>
        </Flex>
      </Card>
    );
  }

  return (
    <div className="space-y-6 bg-gray-50 p-4 rounded-xl">
      {/* KPI Cards Row */}
      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        {/* Overall Risk Score Card */}
        <Card decoration="top" decorationColor={riskInfo.color} className="!bg-white">
          <Flex alignItems="start" justifyContent="between">
            <div>
              <Text className="!text-gray-600">{t('dashboard.workshop4.overallRiskScore')}</Text>
              <Metric className="!text-gray-900">{overallRiskScore}/10</Metric>
              <BadgeDelta deltaType={riskInfo.deltaType} size="xs" className="mt-1">
                {riskInfo.label}
              </BadgeDelta>
            </div>
            <div className="p-2 bg-gray-100 rounded-lg">
              <Zap className="w-6 h-6 text-gray-600" />
            </div>
          </Flex>
        </Card>

        {/* Attack Paths Card */}
        <Card decoration="top" decorationColor="violet" className="!bg-white">
          <Flex alignItems="start" justifyContent="between">
            <div>
              <Text className="!text-gray-600">{t('dashboard.workshop4.attackPaths')}</Text>
              <Metric className="!text-gray-900">{totalAttackPaths}</Metric>
              <Text className="mt-1 text-xs !text-gray-500">
                {t('dashboard.workshop4.pathsIdentified')}
              </Text>
            </div>
            <div className="p-2 bg-violet-100 rounded-lg">
              <Target className="w-6 h-6 text-violet-600" />
            </div>
          </Flex>
        </Card>

        {/* Operational Scenarios Card */}
        <Card decoration="top" decorationColor="indigo" className="!bg-white">
          <Flex alignItems="start" justifyContent="between">
            <div>
              <Text className="!text-gray-600">{t('dashboard.workshop4.operationalScenarios')}</Text>
              <Metric className="!text-gray-900">{totalScenarios}</Metric>
              <Text className="mt-1 text-xs !text-gray-500">
                {t('dashboard.workshop4.scenariosAnalyzed')}
              </Text>
            </div>
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Activity className="w-6 h-6 text-indigo-600" />
            </div>
          </Flex>
        </Card>

        {/* High Risk Assets Card */}
        <Card decoration="top" decorationColor="rose" className="!bg-white">
          <Flex alignItems="start" justifyContent="between">
            <div>
              <Text className="!text-gray-600">{t('dashboard.workshop4.highRiskAssets')}</Text>
              <Metric className="!text-gray-900">{highRiskAssets}</Metric>
              {highRiskAssets > 0 && (
                <Badge color="rose" size="xs" className="mt-1">
                  {t('dashboard.workshop4.requiresAction')}
                </Badge>
              )}
            </div>
            <div className="p-2 bg-rose-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-rose-600" />
            </div>
          </Flex>
        </Card>
      </Grid>

      {/* Charts Grid (draggable/resizable in edit mode) */}
      <DashboardGrid
        items={[
          {
            id: 'w4-riskMatrix',
            fullscreenTitle: t('dashboard.workshop4.riskMatrix'),
            fullscreenContent: (<EChart option={riskMatrixOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop4.riskMatrix')}</Title>
                {riskMatrix.defaultedCount > 0 && (
                  <Text className="text-xs !text-gray-500 mt-1">{t('dashboard.workshop4.defaultedNote')}</Text>
                )}
                {riskMatrix.total > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: 136 }}><EChart option={riskMatrixOption} height="100%" /></div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop4.noScenarios')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          {
            id: 'w4-coverage',
            fullscreenTitle: t('dashboard.workshop4.coverage'),
            fullscreenContent: (<EChart option={coverageOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop4.coverage')}</Title>
                {coverage.total > 0 ? (
                  <div className="flex-1 min-h-0" style={{ minHeight: 90 }}><EChart option={coverageOption} height="100%" /></div>
                ) : totalAttackPaths > 0 ? (
                  <div className="h-60 flex items-center justify-center px-4 text-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop4.noRetainedPaths')}</Text>
                  </div>
                ) : (
                  <div className="h-60 flex items-center justify-center">
                    <Text className="!text-gray-400">{t('dashboard.workshop4.noData')}</Text>
                  </div>
                )}
              </Card>
            )
          },
          ...(skillMatrix.hasData ? [{
            id: 'w4-skillHeatmap',
            fullscreenTitle: t('dashboard.workshop4.severitySkillHeatmap'),
            fullscreenContent: (<EChart option={skillHeatmapOption} height={560} />),
            element: (
              <Card className="!bg-white h-full flex flex-col">
                <Title className="!text-gray-900">{t('dashboard.workshop4.severitySkillHeatmap')}</Title>
                <Text className="text-xs !text-gray-500 mt-1">{t('dashboard.workshop4.severitySkillSubtitle')}</Text>
                <div className="flex-1 min-h-0" style={{ minHeight: 95 }}><EChart option={skillHeatmapOption} height="100%" /></div>
              </Card>
            )
          }] : [])
        ]}
        defaultLayout={W4_DEFAULT_LAYOUT}
        editMode={editMode}
        layouts={layouts}
        onLayoutChange={onLayoutChange}
        onToggleHidden={onToggleHidden}
      />
    </div>
  );
};

export default Workshop4Dashboard;
